import React, { useContext, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField
} from "@material-ui/core";
import LockOutlined from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import LocationOnOutlined from "@material-ui/icons/LocationOnOutlined";
import ApartmentOutlined from "@material-ui/icons/ApartmentOutlined";
import LocationCityOutlined from "@material-ui/icons/LocationCityOutlined";
import FlagOutlined from "@material-ui/icons/FlagOutlined";
import DraftsOutlined from "@material-ui/icons/DraftsOutlined";
import CheckCircle from "@material-ui/icons/CheckCircle";
import Edit from "@material-ui/icons/Edit";
import LocaleContext from "../../../../i18n/LocaleContext";
import { currencyInput, formatCurrencyInput } from "../values";

export const useTranslation = () => useContext(LocaleContext);

const translated = (t, children) =>
  typeof children === "string" ? t(children) : children;

export function StepHeader({ title, detail, children, icon: Icon, align }) {
  const t = useTranslation();
  const left = align === "left";
  // A centered heading keeps its icon inline before the title, as on the
  // mockups; a left heading shows it as a badge beside the whole block.
  const icon = Icon && (
    <span className="merchant-heading-icon">
      <Icon aria-hidden="true" />
    </span>
  );
  return (
    <header className={`merchant-step-heading${left ? " merchant-heading-left" : ""}`}>
      {left && icon}
      <div>
      <h1>
        {!left && icon}
        {t(title)}
      </h1>
      {children && <p>{translated(t, children)}</p>}
      {detail && <p className="merchant-step-detail">{t(detail)}</p>}
      </div>
    </header>
  );
}

export function Section({ title, children }) {
  const t = useTranslation();
  return (
    <section className="merchant-section">
      <h2>{t(title)}</h2>
      {children}
    </section>
  );
}

// `featured` shows the icon in a round badge and a serif title, `checks`
// adds a short list of benefits on the right, as on the step mockups.
export function InfoPanel({
  title,
  children,
  icon: Icon = LockOutlined,
  featured,
  checks
}) {
  const t = useTranslation();
  return (
    <div
      className={`merchant-info${featured ? " merchant-info-featured" : ""}`}
    >
      <span className="merchant-info-icon">
        <Icon aria-hidden="true" />
      </span>
      <div className="merchant-info-text">
        <strong>{t(title)}</strong>
        {children && <p>{translated(t, children)}</p>}
      </div>
      {checks && (
        <ul className="merchant-checks">
          {checks.map(check => {
            // A trailing "(...)" note goes on its own line.
            const [text, note] = t(check).split(/ (?=\()/);
            return (
              <li key={check}>
                <CheckCircle aria-hidden="true" />
                <span>
                  {text}
                  {note && <small>{note}</small>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function SecureNote() {
  const t = useTranslation();
  return (
    <InfoPanel title="Your information is secure.">
      <>
        {t(
          "We use industry-standard encryption to keep your information safe and private."
        )}
        <br />
        {t("We will never share or sell your information.")}
      </>
    </InfoPanel>
  );
}

export function Field({
  name,
  label,
  application,
  errors = {},
  onApplicationTextChange,
  icon: Icon,
  options,
  secret,
  inputProps,
  InputProps,
  helperText,
  placeholder,
  ...props
}) {
  const t = useTranslation();
  const [visible, setVisible] = useState(false);
  const classes = ["merchant-field"];
  if (Icon) classes.push("merchant-field-icon");
  if (!label) classes.push("merchant-field-nolabel");
  return (
    <TextField
      id={`merchant-${name}`}
      name={name}
      fullWidth
      variant="outlined"
      className={classes.join(" ")}
      label={label ? t(label) : undefined}
      value={application[name] == null ? "" : application[name]}
      onChange={onApplicationTextChange(name)}
      error={!!errors[name]}
      helperText={errors[name] || (helperText && t(helperText))}
      select={!!options}
      placeholder={placeholder && t(placeholder)}
      type={secret && !visible ? "password" : "text"}
      InputLabelProps={{ shrink: true }}
      inputProps={{ ...inputProps }}
      InputProps={{
        notched: false,
        startAdornment: Icon && (
          <InputAdornment position="start">
            <Icon />
          </InputAdornment>
        ),
        endAdornment: secret && (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label={`${t(visible ? "Hide" : "Show")} ${t(label)}`}
              onClick={() => setVisible(!visible)}
            >
              {visible ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
        ...InputProps
      }}
      {...props}
    >
      {options &&
        options.map(option => (
          <MenuItem key={option.value || option} value={option.value || option}>
            {t(option.label || option)}
          </MenuItem>
        ))}
    </TextField>
  );
}

export function CurrencyField({
  name,
  application,
  onApplicationTextChange,
  ariaLabel,
  ...props
}) {
  const [focused, setFocused] = useState(false);
  const input = useRef(null);
  const raw = application[name] == null ? "" : String(application[name]);
  const display = formatCurrencyInput(raw, !focused);
  const change = () => event => {
    const text = event.target.value;
    const next = currencyInput(text);
    if (next === null) return;
    const offset = (
      text.slice(0, event.target.selectionStart).match(/[0-9.]/g) || []
    ).length;
    onApplicationTextChange(name)({ target: { value: next } });
    requestAnimationFrame(() => {
      if (!input.current || document.activeElement !== input.current) return;
      let count = 0;
      let caret = 0;
      while (caret < input.current.value.length && count < offset) {
        if (/[0-9.]/.test(input.current.value[caret])) count += 1;
        caret += 1;
      }
      input.current.setSelectionRange(caret, caret);
    });
  };
  return (
    <Field
      {...props}
      name={name}
      application={{ [name]: display }}
      onApplicationTextChange={change}
      inputRef={input}
      inputProps={{ inputMode: "decimal", "aria-label": ariaLabel }}
      InputProps={{
        startAdornment: <InputAdornment position="start">$</InputAdornment>
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (raw.endsWith("."))
          onApplicationTextChange(name)({
            target: { value: raw.slice(0, -1) }
          });
      }}
    />
  );
}

export function SameAddress({
  application,
  name,
  onCheckChange,
  label,
  badge,
  description
}) {
  const t = useTranslation();
  return (
    <div className="merchant-same-address">
      <FormControlLabel
        label={
          <span className="merchant-same-address-label">
            <span>
              {t(label)}
              {badge && <span className="merchant-badge">{t(badge)}</span>}
            </span>
            {description && <small>{t(description)}</small>}
          </span>
        }
        control={
          <Checkbox
            color="primary"
            checked={!application[name]}
            onChange={event =>
              onCheckChange(name)({
                target: { checked: !event.target.checked }
              })
            }
          />
        }
      />
    </div>
  );
}

export const STATES = "AL AK AZ AR AS CA CO CT DE DC FL GA GU HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA PR RI SC SD TN TX TT UT VT VA VI WA WV WI WY MP".split(
  " "
);

// Old data can hold a lower-case state; the select only knows upper case.
export const withUpperState = (application, name) => ({
  ...application,
  [name]: (application[name] || "").toUpperCase()
});

export function AddressFields({ prefix, streetIcon, ...props }) {
  const address = prefix === "billing" ? "billingAddress" : `${prefix}Address1`;
  return (
    <div className="merchant-grid">
      <div className="merchant-full">
        <Field
          {...props}
          name={address}
          label="Street Address"
          icon={streetIcon || LocationOnOutlined}
          required
          autoComplete="street-address"
        />
      </div>
      {prefix !== "billing" && (
        <div className="merchant-full">
          <Field
            {...props}
            name={`${prefix}Address2`}
            label="Suite, Unit or Apt. (optional)"
            icon={ApartmentOutlined}
          />
        </div>
      )}
      <div className="merchant-address-row merchant-full">
        <Field
          {...props}
          name={`${prefix}City`}
          label="City"
          required
          icon={LocationCityOutlined}
        />
        <Field
          {...props}
          application={withUpperState(props.application, `${prefix}State`)}
          name={`${prefix}State`}
          label="State"
          required
          icon={FlagOutlined}
          options={STATES}
        />
        <Field
          {...props}
          name={`${prefix}Zip`}
          label="Zip Code"
          required
          icon={DraftsOutlined}
          inputProps={{ inputMode: "numeric", maxLength: 5 }}
        />
      </div>
    </div>
  );
}

export function EditButton({ onClick, label = "Edit", icon }) {
  const t = useTranslation();
  return (
    <Button
      variant="outlined"
      color="primary"
      size="small"
      startIcon={icon && <Edit />}
      onClick={onClick}
    >
      {t(label)}
    </Button>
  );
}
